import {FontAwesomeIcon as Icon} from "@fortawesome/react-fontawesome"
import {Panel} from "components/Panel"
import React from "react"
import {connect} from "react-redux"
import Trans from '../../components/Trans'
import {GlobalState} from "../../state"
import SpeedAuktionDetails, {SpeedAuktionRound} from "./SpeedAuktionDetails"
import SpeedAuktionCard from "./SpeedAuktionCard"
import AppNavigation from "../AppNavigation"

type SpeedAuktionPageProps = {}

type SpeedAuktionPageState = {
  busy: boolean
  claiming: number | null
  rounds: SpeedAuktionRound[]
  error: string | null
  selectedRound: SpeedAuktionRound | null
}

class SpeedAuktionPage extends React.Component<SpeedAuktionPageProps, SpeedAuktionPageState> {
  private countdownInterval: NodeJS.Timeout | null = null

  constructor(props: SpeedAuktionPageProps) {
    super(props)
    this.state = {
      busy: false,
      claiming: null,
      rounds: [],
      error: null,
      selectedRound: null
    }
  }

  componentDidMount() {
    this.loadRounds()
    // Update countdown every second
    this.countdownInterval = setInterval(() => this.forceUpdate(), 1000)
    // Listen for real-time refresh events
    window.addEventListener('speed-auktion-refresh', this.loadRounds)
  }

  componentWillUnmount() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
    window.removeEventListener('speed-auktion-refresh', this.loadRounds)
  }

  loadRounds = async () => {
    this.setState({busy: true, error: null})
    try {
      const response = await window.axios.get<{data: SpeedAuktionRound[], count: number}>('/api/speed-auktion')
      this.setState({
        rounds: response.data?.data || [],
        busy: false
      })
    } catch (err) {
      this.setState({
        busy: false,
        error: 'Fehler beim Laden der Speed-Auktionen',
      })
    }
  }

  claimRound = async (e: React.MouseEvent, roundId: number) => {
    e.preventDefault()
    e.stopPropagation()
    this.setState({claiming: roundId, error: null})
    try {
      await window.axios.post(`/api/speed-auktion/${roundId}/claim`)
      // Reload rounds after claiming
      await this.loadRounds()
    } catch (err) {
      const axiosErr = err as any
      this.setState({
        error: axiosErr?.response?.data?.message || 'Fehler beim Beanspruchen'
      })
    } finally {
      this.setState({claiming: null})
    }
  }

  closeDetails = () => {
    this.setState({selectedRound: null})
  }

  claimFromDetails = async (roundId: number) => {
    this.setState({claiming: roundId, error: null})
    try {
      await window.axios.post(`/api/speed-auktion/${roundId}/claim`)
      this.setState({selectedRound: null})
      await this.loadRounds()
    } catch (err) {
      const axiosErr = err as any
      this.setState({
        error: axiosErr?.response?.data?.message || 'Fehler beim Beanspruchen'
      })
    } finally {
      this.setState({claiming: null})
    }
  }

  render() {
    const {busy, rounds, error, claiming, selectedRound} = this.state

    return (
      <div className="block md:flex overflow-x-auto overflow-y-scroll scrolling-smooth scrolling-touch h-full">
        {/* Speed-Auktion Details Modal */}
        {selectedRound && (
          <SpeedAuktionDetails
            round={selectedRound}
            onClose={this.closeDetails}
            onClaim={this.claimFromDetails}
            claiming={claiming === selectedRound.Id}
          />
        )}

        {/* Sidebar Navigation */}
        <div className="hidden lg:block">
          <AppNavigation/>
        </div>

        <div className="w-full flex flex-col p-4">
        <div className="max-w-7xl mx-auto w-full">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <Icon icon="bolt" className="text-yellow-500 mr-2"/>
              <Trans>Speed-Auktion</Trans>
            </h1>
            <p className="text-gray-600 mt-1">
              <Trans>Beanspruchen Sie Fahrzeuge von Ihren Partnerhändlern</Trans>
            </p>
          </div>

          {/* Error display */}
          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
              <button onClick={() => this.setState({error: null})} className="float-right">
                <Icon icon="times"/>
              </button>
            </div>
          )}

          {busy ? (
            <div className="flex justify-center items-center py-12">
              <Icon icon="spinner" spin className="text-4xl text-gray-400"/>
            </div>
          ) : rounds.length === 0 ? (
            <Panel>
              <div className="p-8 text-center text-gray-500">
                <Icon icon="inbox" className="text-6xl mb-4"/>
                <p><Trans>Keine Speed-Auktionen verfügbar</Trans></p>
                <p className="text-sm mt-2">
                  <Trans>Wenn ein Gebot akzeptiert wird und Speed-Auktion aktiviert ist, erscheinen hier Fahrzeuge zum Beanspruchen.</Trans>
                </p>
              </div>
            </Panel>
          ) : (
            <div className="flex flex-wrap -mx-2">
              {rounds.map(round => (
                <SpeedAuktionCard
                  key={round.Id}
                  round={round}
                  onShowDetails={(r) => this.setState({selectedRound: r})}
                  onClaim={(roundId) => this.claimRound({preventDefault: () => {}, stopPropagation: () => {}} as React.MouseEvent, roundId)}
                  claiming={claiming === round.Id}
                />
              ))}
            </div>
          )}
        </div>
        </div>
      </div>
    )
  }
}

export default connect((state: GlobalState) => ({}))(SpeedAuktionPage)
